/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import NodeTouchActionComponent from "./Action/NodeTouchActionComponent";

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Component grouped together by common node to maintain singular visibility. */
@cc._decorator.ccclass
export default class GroupedMenuComponent extends cc.Component
{
    /** Map that groups components to node. */
    public static GroupedInstances: Map<cc.Node, GroupedMenuComponent[]> =
        new Map<cc.Node, GroupedMenuComponent[]>();

    /** Node which visibility will be affected. */
    @property({ type: cc.Node, visible: true })
    private _controlledNode: cc.Node = null;
    /** Public setter for controlled node. */
    public set ControlledNode(value: cc.Node) { this._controlledNode = value; }

    /** Top level ancestor to dismiss when off tap. */
    @property({ type: cc.Node, visible: true })
    private _topNode: cc.Node = null;
    /** Public setter for top level ancestor. */
    public set TopNode(value: cc.Node) { this._topNode = value; }

    /** Node which groups singularity of visibility. */
    @property({ type: cc.Node, visible: true })
    private _groupingNode: cc.Node = null;
    /** Public setter for grouping node. */
    public set GroupingNode(value: cc.Node) { this._groupingNode = value; }

    /** If true, should activate if deactive on click. */
    @property({ visible: true })
    private _activate: boolean = true;

    /** If true, should deactivate if active on click. */
    @property({ visible: true })
    private _deactivate: boolean = true;

    /** If true, will set visibility to false. */
    @property({ visible: true })
    private _hideOnLoad: boolean = true;

    /** @hidden */
    protected onLoad(): void
    {
        if (this._groupingNode && this._controlledNode)
        {
            this.addInstanceToGrouping();
        }

        this.node.on(cc.Node.EventType.TOUCH_END, this.onTouchEndHandler.bind(this));

        // Allow for visibility in editor without affecting game.
        if (this._hideOnLoad && this._controlledNode)
        {
            this._controlledNode.active = false;
        }
    }

    /** @hidden */
    protected start(): void
    {
        this.configureCanvasTouchHandler();
    }

    /** Configures handler to toggle off on canvas touch. */
    private configureCanvasTouchHandler(): void
    {
        let offTouchAction: NodeTouchActionComponent = this.node.addComponent(NodeTouchActionComponent);
        offTouchAction.TargetNode = this._topNode || cc.find('Canvas');
        offTouchAction.Action = this.onDismissTouchEndHandler;
    }

    /** Hide menu of all components who share same group component. */
    public DeactivateGroup(): void
    {
        if (!this._groupingNode) { return; }

        let grouped: GroupedMenuComponent[] = GroupedMenuComponent.GroupedInstances.get(this._groupingNode);

        if (!grouped) { return; }

        grouped
            .filter(x => x.node.active)
            .forEach(x => x.deactivate());
    }

    /** Adds current instance to node grouping. */
    private addInstanceToGrouping(): GroupedMenuComponent
    {
        // Check if grouping exists, if not add new array with THIS component.
        if (!GroupedMenuComponent.GroupedInstances.has(this._groupingNode))
        {
            GroupedMenuComponent.GroupedInstances.set(this._groupingNode, [this]);
            return this;
        }

        // Found list, push it in there.
        GroupedMenuComponent.GroupedInstances.get(this._groupingNode).push(this);
        return this;
    }

    /** Handler for touch end event. */
    private onTouchEndHandler = (event: cc.Event.EventTouch): void =>
    {
        if (!this._controlledNode)
        {
            // Always deactivate those in group.
            this.DeactivateGroup();
            return;
        }

        let isActive: boolean = this._controlledNode.active;
        this.DeactivateGroup();

        event.bubbles = this._topNode === null || !isActive;

        // Is this a toggle?
        if (this._activate && this._deactivate)
        {
            this._controlledNode.active = !isActive;
            return;
        }

        if (this._activate)
        {
            this._controlledNode.active = true;
        }

        if (this._deactivate)
        {
            this._controlledNode.active = false;
        }
    }

    /** Handler for activation of a different [[GroupedMenuComponent]] in same group. */
    private onDismissTouchEndHandler = (event: cc.Event.EventTouch): void =>
    {
        if (event.target === this.node) { return; }

        this.deactivate();
    }

    /** Hide controlled node. */
    private deactivate = (): void =>
    {
        if (!this._controlledNode) { return; }

        this._controlledNode.active = false;
    }
}
